// The chat leg of the site backend transport (creative-code-studio-chatbot-prd
// D-CB-06/07/11): what the parent does with ONE `ai.chat(text)` from the
// sandboxed page. Shared by the studio `SiteFrame` (authed per-project reply
// endpoint, the kid's wallet) and the public `ReadOnlySiteFrame` (no-auth
// per-share endpoint, the author's visitor budget) — the ONLY difference is the
// `send` function each host injects.
//
// The parent OWNS the transcript: it appends the user's line before the call
// and the bot's line after a successful reply (removing the user's line on
// failure), persists it device-locally, and sends the server only the last few
// messages as history (D-CB-08). A read-only host (teacher live view, parent
// read-only studio) answers VIEW_ONLY LOCALLY — no request ever leaves.

import { ApiError } from '@/lib/api';
import type { SiteChatErrorCode, SiteChatResult } from './buildSitePreview';
import type { ChatbotTranscript } from './chatbotTranscript';

/** A typed `ai.chat` failure the frame turns into an Error with a `code`. */
export class SiteChatError extends Error {
  constructor(
    public readonly code: SiteChatErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'SiteChatError';
  }
}

/** Kid-readable default line per typed code (the page usually shows its own). */
export const SITE_CHAT_ERROR_COPY: Record<SiteChatErrorCode, string> = {
  OUT_OF_STARS: 'Out of Stars! Ask a parent to top up.',
  BUDGET_EXHAUSTED: 'This bot has used up its visitor Stars for now.',
  DAILY_CAP: "You've reached today's Stars limit. Try again tomorrow!",
  MESSAGE_BLOCKED: 'That message is not something this bot can chat about.',
  VIEW_ONLY: 'Chat is off in view mode.',
  AI_UNAVAILABLE: 'The bot is having a nap. Try again in a moment (no Stars were charged).',
  PAUSED: 'AI is paused right now.',
  BUSY: 'One message at a time — wait for the reply!',
};

/**
 * Map a backend failure (the `ApiError` the api client / public fetch raises)
 * to the typed code the page understands. Every raw backend code lands on ONE
 * of the eight page codes — the page never sees jargon (user-facing-errors rule).
 */
export function mapChatApiError(err: unknown): SiteChatError {
  if (err instanceof SiteChatError) return err;
  if (!(err instanceof ApiError)) {
    return new SiteChatError('AI_UNAVAILABLE', SITE_CHAT_ERROR_COPY.AI_UNAVAILABLE);
  }
  const details = (err.details ?? {}) as { kind?: string };
  switch (err.code) {
    case 'WALLET_INSUFFICIENT':
      // A cap block (daily/weekly/monthly) reads as "today's limit"; a balance or
      // per-request block is the out-of-Stars beat.
      return details.kind && details.kind !== 'balance' && details.kind !== 'per_request'
        ? new SiteChatError('DAILY_CAP', SITE_CHAT_ERROR_COPY.DAILY_CAP)
        : new SiteChatError('OUT_OF_STARS', SITE_CHAT_ERROR_COPY.OUT_OF_STARS);
    case 'BUDGET_EXHAUSTED':
      return new SiteChatError('BUDGET_EXHAUSTED', SITE_CHAT_ERROR_COPY.BUDGET_EXHAUSTED);
    case 'DAILY_CAP':
    case 'DAILY_CAP_EXCEEDED':
      return new SiteChatError('DAILY_CAP', SITE_CHAT_ERROR_COPY.DAILY_CAP);
    case 'MESSAGE_BLOCKED':
    case 'MODERATION_REJECTED':
    case 'MODERATION_WARN':
      return new SiteChatError('MESSAGE_BLOCKED', SITE_CHAT_ERROR_COPY.MESSAGE_BLOCKED);
    case 'VIEW_ONLY':
      return new SiteChatError('VIEW_ONLY', SITE_CHAT_ERROR_COPY.VIEW_ONLY);
    case 'PAUSED':
    case 'FAMILY_PAUSED':
    case 'CHATBOT_PUBLIC_DISABLED':
      return new SiteChatError('PAUSED', SITE_CHAT_ERROR_COPY.PAUSED);
    case 'AI_PRICE_CHANGED':
      return new SiteChatError('AI_UNAVAILABLE', 'The Star price just changed — send that again.');
    default:
      return new SiteChatError('AI_UNAVAILABLE', SITE_CHAT_ERROR_COPY.AI_UNAVAILABLE);
  }
}

/** What each host injects: the actual backend call for ONE message. */
export type SiteChatSend = (
  text: string,
  history: Array<{ role: 'user' | 'bot'; text: string }>,
) => Promise<SiteChatResult>;

/** The chat leg of `SiteBackendTransport`. */
export interface SiteChatTransport {
  /** ONE `ai.chat(text)`: transcript bookkeeping + the backend call. */
  send(text: string): Promise<SiteChatResult>;
  /** `ai.clear()`: wipe the device-local transcript. */
  clear(): void;
}

export interface CreateSiteChatTransportArgs {
  transcript: ChatbotTranscript;
  send: SiteChatSend;
  /** Read-only host (D-CB-11): answer VIEW_ONLY locally, never call. */
  readOnly?: boolean;
  /** A reply was DELIVERED (and charged) — refetch the balance / budget meter. */
  onReply?: (result: SiteChatResult) => void;
  /** The RAW backend code behind a failure (e.g. `WALLET_INSUFFICIENT` so the
   *  studio can raise the Out-of-Stars sheet). Typed codes still go to the page. */
  onError?: (rawCode: string, mapped: SiteChatError) => void;
}

export function createSiteChatTransport(args: CreateSiteChatTransportArgs): SiteChatTransport {
  const { transcript } = args;
  return {
    async send(text) {
      if (args.readOnly) {
        throw new SiteChatError('VIEW_ONLY', SITE_CHAT_ERROR_COPY.VIEW_ONLY);
      }
      await transcript.init();
      // History EXCLUDES the message being sent — the server appends it itself.
      const history = transcript.historyForServer();
      const line = transcript.append('user', text);
      try {
        const result = await args.send(text, history);
        transcript.append('bot', result.reply);
        args.onReply?.(result);
        return result;
      } catch (err) {
        transcript.remove(line);
        const mapped = mapChatApiError(err);
        const raw = err instanceof ApiError ? err.code : mapped.code;
        args.onError?.(raw, mapped);
        throw mapped;
      }
    },
    clear() {
      transcript.clear();
    },
  };
}
